import AppPage from "../AppPage";
import MenuBottomBar, { MenuBarItem } from "../MenuBottomBar";
import { globals, images } from "../../styles";
import { arrow_Icon, crown_Icon, trophy_Icon } from "../../assets/icons";
import { useNavigate } from "../../hooks/useNavigate";
import { Page } from "../../types";
import { useUserId } from "../../hooks/useUserId";
import styles from "./LeaderboardSelectionPage.module.css";
import { useCurrentDraft } from "../../hooks/drafts";
import { useGroupLeaderboard } from "../../hooks/groups";
import { usePlayerStats } from "../../hooks/player";
import { LeaderboardEntry } from "../../api/types";
import LoadingSpinner from "../LoadingSpinner";
import { pointsFormatter } from "../../helpers";
import { useMemo } from "react";

interface LeaderboardSectionProps {
  title: string;
  subtitle?: string;
  topMembers: LeaderboardEntry[];
  currentPlayerRank: number;
  currentPlayerScore: number;
  onViewDetails?: () => void;
}

function LeaderboardSection(props: LeaderboardSectionProps) {
  const {
    title,
    subtitle,
    topMembers,
    currentPlayerRank,
    currentPlayerScore,
    onViewDetails,
  } = props;
  if (topMembers.length < 3) {
    return null;
  }

  const first = topMembers[0];
  const second = topMembers[1];
  const third = topMembers[2];

  return (
    <>
      <span className={globals["leaderboard-section-heading"]}>{title}</span>
      {subtitle && (
        <span className={styles["section-subtitle"]}>{subtitle}</span>
      )}
      <div className={styles["leaderboard-top-section"]}>
        <div className={styles["podium"]}>
          {/* 2nd Place */}
          <div className={styles["podium-item"]}>
            <div className={styles["crown-wrapper"]}>
              <img
                src={crown_Icon}
                className={images["crown-icon--leaderboard"]}
                alt=""
              />
              <span className={styles["rank-number"]}>2</span>
            </div>
            <span className={styles["username"]}>
              {second?.username ?? "—"}
            </span>
            <span className={styles["score"]}>{second?.score ?? "—"}</span>
          </div>
          {/* 1st Place */}
          <div
            className={`${styles["podium-item"]} ${styles["podium-item--first"]}`}
          >
            <div className={styles["crown-wrapper"]}>
              <img
                src={crown_Icon}
                className={images["crown-icon--first"]}
                alt=""
              />
              <span className={styles["rank-number--first"]}>1</span>
            </div>
            <span
              className={`${styles["username"]} ${styles["username--first"]}`}
            >
              {first?.username ?? "—"}
            </span>
            <span className={`${styles["score"]} ${styles["score--first"]}`}>
              {first?.score ?? "—"}
            </span>
          </div>
          {/* 3rd Place */}
          <div className={styles["podium-item"]}>
            <div className={styles["crown-wrapper"]}>
              <img
                src={crown_Icon}
                className={images["crown-icon--leaderboard"]}
                alt=""
              />
              <span className={styles["rank-number"]}>3</span>
            </div>
            <span className={styles["username"]}>{third?.username ?? "—"}</span>
            <span className={styles["score"]}>{third?.score ?? "—"}</span>
          </div>
        </div>
        {currentPlayerRank && currentPlayerScore >= 0 && (
          <div className={styles["your-rank-card"]}>
            <div className={styles["your-rank-top"]}>
              <img
                src={crown_Icon}
                className={images["username-crown"]}
                alt=""
              />
              <span className={styles["your-rank-label"]}>
                Your Leaderboard Stats
              </span>
              <img
                src={crown_Icon}
                className={images["username-crown"]}
                alt=""
              />
            </div>
            <div className={styles["your-rank-bottom"]}>
              <span className={styles["your-rank-value"]}>
                Rank: {currentPlayerRank}
              </span>
              <span className={styles["your-rank-points"]}>
                Points: {pointsFormatter(currentPlayerScore)}
              </span>
            </div>
          </div>
        )}
        <button
          className={styles["leaderboard-nav-btn"]}
          onClick={onViewDetails}
        >
          <div className={styles["leaderboard-nav-btn-icon"]}>
            <img
              src={trophy_Icon}
              alt="leaderboard"
              className={images["artist-icon"]}
            />
          </div>
          <span className={styles["leaderboard-nav-btn-label"]}>
            View Leaderboard Details
          </span>
          <img
            src={arrow_Icon}
            alt="go"
            className={styles["leaderboard-nav-btn-arrow"]}
          />
        </button>
      </div>
    </>
  );
}

function LeaderboardSelectionPage(props: {
  setSelectedGroupId: (id: string | undefined) => void;
}) {
  const { setSelectedGroupId } = props;
  const { navigate } = useNavigate();

  const userId = useUserId();
  const { data: currentDraft } = useCurrentDraft();
  const worldLeaderboardId = currentDraft?.leaderboardGroupId ?? undefined;
  const { data: worldLeaderboardData, isPending: isWorldLeaderboardLoading } =
    useGroupLeaderboard(worldLeaderboardId, {
      limit: 3,
      offset: 0,
    });
  const { data: playerStats, isPending: isPlayerStatsLoading } =
    usePlayerStats(userId);
  const currentPlayerRank = useMemo(
    () =>
      playerStats?.ranks.find((r) => r.groupId === worldLeaderboardId)?.rank ??
      0,
    [playerStats?.ranks, worldLeaderboardId],
  );

  const toLeaderboardDetails = () => {
    // TODO: Fix for groups
    // Confused about flow... think this is right?
    setSelectedGroupId(worldLeaderboardId);
    navigate(Page.ExpandedLeaderboard);
  };

  const isLoading = isPlayerStatsLoading || isWorldLeaderboardLoading;

  return (
    <AppPage
      className={globals["page"]}
      bottomBar={
        <MenuBottomBar
          items={[
            MenuBarItem.Home,
            MenuBarItem.News,
            MenuBarItem.Groups,
            MenuBarItem.LogOut,
          ]}
        />
      }
    >
      <button
        className={`${globals["button-wrapper"]} ${globals["back-btn--top-left"]}`}
        onClick={() => navigate(Page.Welcome)}
      >
        <div className={globals["back-btn"]}>
          <img src={arrow_Icon} alt="" className={images["back-arrow-icon"]} />
        </div>
      </button>
      <span className={globals["leaderboard-selection-heading"]}>
        Leaderboards
      </span>
      <div className={styles["scroll-container"]}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <LeaderboardSection
            title="Global"
            topMembers={worldLeaderboardData?.leaderboard ?? []}
            currentPlayerRank={currentPlayerRank}
            currentPlayerScore={playerStats?.totalScore ?? 0}
            onViewDetails={toLeaderboardDetails}
          />
        )}
        {/* {userGroups?.map((group) => {
          const userMember = group.members.find((m) => m.id === userId);
          const groupTopMembers = [...group.members]
            .sort((a, b) => a.rank - b.rank)
            .slice(0, 3);
          return (
            <LeaderboardSection
              key={group.id}
              title="Your Groups"
              subtitle={group.name}
              topMembers={groupTopMembers}
              currentUser={
                userMember
                  ? {
                      rank: userMember.rank,
                      username: userMember.username,
                      score: userMember.score,
                    }
                  : currentUserInfo
              }
              onViewDetails={() => navigateToExpanded(group)}
            />
          );
        })} */}
      </div>
    </AppPage>
  );
}

export default LeaderboardSelectionPage;
